import Experience, {
  debugAtom,
  debugStore,
  useSectionStore,
} from "@folio-2026/experience"
import Resources, { Sources } from "@folio-2026/resources"
import EventEmitter from "@folio-2026/shared/eventemitter"
import { TextScene } from "@folio-2026/text"
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

import AppFrame from "./AppFrame"
import Camera from "./Camera"

type SectionProps = {
  sources?: Sources
  name?: string
  folderName?: string
  expanded?: boolean
}
export default class Section extends EventEmitter {
  name: string

  experience: Experience
  scene: THREE.Scene
  sizes: Experience["sizes"]
  resources: Resources
  renderer: Experience["renderer"]
  debug: Experience["debug"]
  camera: Camera | unknown
  debugFolder?: FolderApi
  fbo: THREE.RenderTarget
  controls?: OrbitControls
  controlCamera?: THREE.PerspectiveCamera
  helper?: THREE.CameraHelper
  textScene!: TextScene
  globalResources!: Resources["items"]
  folderName?: string
  expanded: boolean
  appFrame: AppFrame

  effects: Map<string, THREE.Audio> | null = null

  audio!: THREE.Audio
  audioListener!: THREE.AudioListener

  constructor({
    sources = [],
    name,
    folderName,
    expanded = true,
  }: SectionProps = {}) {
    super()

    this.name = name || "section base"

    this.folderName = folderName
    this.expanded = expanded

    this.experience = Experience.getInstance()
    this.renderer = this.experience.renderer
    this.sizes = this.experience.sizes

    this.debug = this.experience.debug

    this.resources = new Resources(sources)
    this.resources.on("ready", () => {
      this.trigger("ready")
    })

    this.scene = new THREE.Scene()
    this.fbo = new THREE.RenderTarget(this.sizes.width, this.sizes.height)

    this.textScene = new TextScene(this.name, {
      perlinTexture: this.experience.globalResources.perlin as THREE.Texture,
    })

    this.appFrame = new AppFrame({
      name: this.name,
      textScene: this.textScene,
    })
  }

  init() {
    this.appFrame.init()

    this.textScene.setDebugFolder(this.debugFolder)

    this.eventListeners()
  }

  eventListeners() {
    useSectionStore.subscribe((state, prevState) => {
      if (
        state.currentSection === this.name &&
        prevState.currentSection !== this.name
      ) {
        this.turnOnEvents()
      }
      if (
        prevState.currentSection === this.name &&
        state.currentSection !== this.name
      ) {
        this.turnOffEvents()
      }
    })

    //
    // First section
    const pathname = window.location.pathname
    // ! this does not work for single section pages
    if ((pathname === "/" || pathname === "/about") && this.name === "loader") {
      this.turnOnEvents()
    }
  }

  turnOnEvents() {
    this.textScene.texts.forEach((text) => {
      text.setupEventListeners()
    })
  }

  turnOffEvents() {
    this.textScene.texts.forEach((text) => {
      text.removeEventListeners()
    })
  }

  /**
   * Set OrbitControls capabilities on this section. It must be called after the camera is set.
   */
  setOrbitControls() {
    if (!this.camera) {
      return
    }
    const camera = this.camera as Camera
    this.controls = new OrbitControls(
      camera.camera,
      this.renderer.instance.domElement,
    )
    // this.controls.target.set(0, 0, 115);
    this.controls.enabled = true
    this.controls.update()
  }

  setPostProcessing() {
    if (!this.camera) {
      return
    }
  }

  playAudio() {
    if (this.audio && !this.audio.isPlaying) {
      this.audio.setLoop(true)
      this.audio.play()
    }
  }

  setVolume(localVolume: number) {
    const volume = localVolume * this.experience.volume

    if (this.audio) {
      this.audio.setVolume(volume)
    }
  }

  createAudio(key: string) {
    const audioLoader = this.experience.audioLoader
    this.audioListener = new THREE.AudioListener()
    this.audio = audioLoader.createAudio(key, this.audioListener) as THREE.Audio
  }

  createEffect(key: string) {
    if (!this.effects) {
      this.effects = new Map()
    }

    const audioLoader = this.experience.audioLoader
    const effect = audioLoader.createAudio(
      key,
      this.audioListener,
    ) as THREE.Audio

    this.effects.set(key, effect)
  }

  resize() {
    if (this.camera) {
      const camera = this.camera as Camera
      camera.resize()
    }

    requestIdleCallback(() => {
      if (this.textScene) {
        this.textScene.resize()
        this.appFrame.resize()
      }
    })
  }

  update(params: { delta: number; elapsed: number }) {
    if (this.textScene) {
      this.textScene.update(params, this.fbo)
    }

    if (this.debug.settings.showOrbitControls && this.controls) {
      if (!this.controls.enabled) {
        this.controls.enabled = true
      }
      this.controls.update()
    } else if (this.controls?.enabled) {
      this.controls.enabled = false
    }
  }

  setDebug() {
    if (this.debug.active && this.debug.ui && this.folderName) {
      this.debugFolder = this.debug.ui.addFolder({
        title: this.folderName,
        expanded: this.expanded,
      })

      debugStore.sub(debugAtom, () => {
        const state = debugStore.get(debugAtom)

        if (state.showOrbitControls) {
          this.setOrbitControls()
          // this.helper = new THREE.CameraHelper(this.camera.camera)
          // this.scene.add(this.helper)
        } else if (this.helper) {
          // this.scene.remove(this.helper)
          // this.helper.dispose()
        }
      })
    }
  }
}
